import { useCallback, useMemo, useState } from 'react';
import intl from 'react-intl-universal';

import useApi from 'shared/hooks/api';
import localData from 'utils/localData';
import { USER_DATA } from 'shared/constants/users';
import { useMaterialUIController } from 'context';
import { getSevenDaysAgo } from 'utils/dateTime';

import { getUserFullName } from 'utils/name';
import { DOLPHIN_API } from 'shared/constants/apis';
import { ServiceType } from 'shared/constants/service';
import { Outlet, useNavigate } from 'react-router-dom';
import HistoryTable from 'components/HistoryTable';
import { getOrganizationLogsColumns } from './organizationLogsColumns';
import { PageLoader, PageError } from 'components';
import { useSnackbar } from 'notistack';
import logger from 'utils/logger';

const log = logger.createLogger('OrganizationLogs');

// Check for common connection error patterns
const CONNECTION_ERROR_PATTERNS = [
  'ECONNREFUSED',
  'ENOTFOUND',
  'ETIMEDOUT',
  'Network Error',
  'timeout',
  'ERR_NETWORK',
  'ERR_INTERNET_DISCONNECTED',
];

const HISTORY = {
  SENT: 'sent',
  RECEIVED: 'received',
};

const OrganizationLogs = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const currentDate = new Date();
  const sevenDaysAgo = getSevenDaysAgo(currentDate);

  // Get the timestamp
  const [timeRangeStart, setTimeRangeStart] = useState(sevenDaysAgo);
  const [timeRangeEnd, setTimeRangeEnd] = useState(currentDate);

  const handleViewLog = useCallback(
    (link) => {
      if (link !== '') {
        const splitedLink = link.split('/');
        let jumpLink = link;
        if (
          splitedLink.includes(HISTORY.SENT) ||
          splitedLink.includes(HISTORY.RECEIVED)
        ) {
          //! The issueId MUST be the last element of the splitedLink
          const issueId = splitedLink[splitedLink.length - 1];
          jumpLink = `/dashboard/browse/issues/${issueId}?serviceType=${ServiceType.SEIMITSU}`;
        }
        navigate(jumpLink);
      }
    },
    [navigate]
  );

  const columns = useMemo(
    () => getOrganizationLogsColumns(handleViewLog),
    [handleViewLog]
  );

  const [{ data, error }] = useApi.useGet(DOLPHIN_API.LOGS, {
    params: {
      organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
      timeRangeStart: timeRangeStart,
      timeRangeEnd: timeRangeEnd,
    },
  });

  const isConnectionError = (data) => {
    if (!data.error) return false;
    const errorMessage = data.message?.toLowerCase() || '';

    return CONNECTION_ERROR_PATTERNS.some((pattern) =>
      errorMessage.includes(pattern.toLowerCase())
    );
  };

  if (!data) return <PageLoader />;
  if (error || isConnectionError(data)) {
    if (isConnectionError(data)) {
      return <PageError errorCode={999} />;
    } else {
      return (
        <PageError
          errorCode={error.response.status}
          customErrorMessage={error.response.data.message}
          customErrorDescription={error.response.data.description}
        />
      );
    }
  }
  const logsData =
    typeof data === 'object' && !Array.isArray(data) && data.logs
      ? data.logs
      : data;

  if (Array.isArray(logsData)) {
    logsData.sort((log1, log2) => {
      const createdTime1 = new Date(log1.createdAt);
      const createdTime2 = new Date(log2.createdAt);
      if (createdTime1 > createdTime2) return -1;
      if (createdTime1 < createdTime2) return 1;
      return 0;
    });
  }
  // TODO: [Privilege-Ignore] modify table to show the manage or staff
  const tableData = logsData.map((log) => {
    const logUserFullName = getUserFullName(
      log.userFirstName,
      log.userLastName
    );
    return {
      createdAt: log.createdAt,
      name: logUserFullName,
      userType: log.userType,
      ip: log.ip,
      content: { type: log.type, contents: log.contents },
      view: log.link,
    };
  });
  const handleRefreshClick = () => {
    if (timeRangeEnd.toDateString() === new Date().toDateString()) {
      setTimeRangeEnd(new Date());
    }
    enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
      variant: 'success',
      autoHideDuration: 3000,
    });
  };

  return (
    <>
      <HistoryTable
        columns={columns}
        data={tableData}
        darkMode={darkMode}
        handleRefreshClick={handleRefreshClick}
        tableTitle={intl.get('logs_text_organization_logs')}
        timeRangeStart={timeRangeStart}
        timeRangeEnd={timeRangeEnd}
        updateTimeRangeStart={setTimeRangeStart}
        updateTimeRangeEnd={setTimeRangeEnd}
        enableRowSelection={false}
      />
      <Outlet />
    </>
  );
};

export default OrganizationLogs;
